import { FC } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm, FormProvider, SubmitHandler, FieldPath } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Spacer } from 'jbc-front/components/presenters/Spacer';
import { Input } from 'jbc-front/components/presenters/form/Input';
import { Required } from 'jbc-front/components/presenters/form/Label';
import { ErrorMessage } from 'jbc-front/components/presenters/form/ErrorMessage';
import Button from 'jbc-front/components/Button';
import { Content } from '@/components/layout/Content';
import { Headding, Item, Panel } from '@/components/PrimaryPanel';
import { MultiSelect } from '@/components/react-hook-form/MultiSelect';
import { useMutation } from '@/hooks/graphql/useMutation';
import { useQuery } from '@/hooks/graphql/useQuery';
import { eachValidationErrorsForHookFrom, ServerValidationError } from '@/hooks/graphql/error/ServerValidationError';
import { useNotify } from '@/hooks/useNotify';
import { laborInsuranceNumber } from '../edit/utils';
import { BasicInformation } from './parts/Form/BasicInformation';
import {
  MASTERS_FOR_CREATE_REPORT,
  MastersForCreateReportQueryResult,
  CREATE_REGULAR_HEALTH_CHECKUP_REPORT,
  CreateRegularHealthCheckupReportQueryVariables,
  CreateRegularHealthCheckupReportQueryResult,
  convertFormDataToQueryVariables
} from './query';
import { Schema, schema } from './schema';

export const CreateReport: FC = () => {
  const notify = useNotify();
  const history = useHistory();

  const methods = useForm<Schema>({
    resolver: yupResolver(schema)
  });
  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors, isSubmitting }
  } = methods;
  const officeId = watch('officeId');
  const officeSnapshot = watch('officeSnapshot');
  const laborInsuranceNumberOfSelectedOffice = laborInsuranceNumber(officeSnapshot);

  const { data, loading } = useQuery<MastersForCreateReportQueryResult>(MASTERS_FOR_CREATE_REPORT, {
    fetchPolicy: 'network-only'
  });
  const aggregationOfficeOptions =
    data && officeId
      ? data.client.offices
          .filter(office => laborInsuranceNumber(office) === laborInsuranceNumberOfSelectedOffice)
          .map(office => ({ value: office.id, label: office.name }))
      : [];

  const [createRegularHealthCheckupReport] = useMutation<
    CreateRegularHealthCheckupReportQueryResult,
    CreateRegularHealthCheckupReportQueryVariables
  >(CREATE_REGULAR_HEALTH_CHECKUP_REPORT);

  const onSubmit: SubmitHandler<Schema> = async data => {
    try {
      const res = await createRegularHealthCheckupReport({
        variables: convertFormDataToQueryVariables(data)
      });

      notify('作成しました', 'success');
      history.push(
        `/regular_health_checkup/reports/${res.data?.createRegularHealthCheckupReport?.regularHealthCheckupReport?.id}`
      );
    } catch (err) {
      if (err instanceof ServerValidationError) {
        eachValidationErrorsForHookFrom(err.validationErrors, schema, data, (path, error) => {
          setError(path as FieldPath<Schema>, error);
        });
        return;
      }
      throw err;
    }
  };

  return (
    <FormProvider {...methods}>
      <div className="l-title-wrap">
        <h1 className="m-title-main">新規作成</h1>
      </div>
      <Content size="xs">
        <Panel>
          <Headding>
            報告書の名称
            <Spacer direction="x" size={8} />
            <Required />
          </Headding>
          <Item>
            <Input {...register('title')} />
            {errors.title?.message && <ErrorMessage>{errors.title.message}</ErrorMessage>}
          </Item>
        </Panel>

        <BasicInformation
          mode="create"
          loading={loading}
          offices={data?.client?.offices}
          prefectures={data?.prefectures}
          businessTypes={data?.businessTypes}
        />

        <Panel>
          <Headding>集計に含める事業所</Headding>
          <Item>
            <MultiSelect
              name="aggregationOfficeIds"
              options={aggregationOfficeOptions}
              disabled={loading}
              isLoading={loading}
              noOptionsMessage={() =>
                officeId ? '適用事業所が見つかりません' : '基本情報の適用事業所を選択してください'
              }
            />
          </Item>
        </Panel>

        <div className="u-ta-c u-mt30">
          <Button primary onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
            保存
          </Button>
        </div>
      </Content>
    </FormProvider>
  );
};
